import React, { useEffect } from 'react';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useOrganizationSubscription } from 'Shared/utils/hooks/use-organization-subscription';
import { useApp } from 'Shared/utils/hooks/use-app';
import { useToggle } from 'Shared/utils/hooks';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Components
import { WpModal, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { WpText } from 'Wpds/components/text';
import { WpLink } from 'Wpds/components/link';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';

// utils
import { trackEvent } from 'Shared/tracking/tracking';

// Styles
import './CharmaLegacySunsetModal.scss';

type Props = {
  onDismiss: Function;
};
const CharmaLegacySunsetModal = ({ onDismiss }: Props) => {
  const [isClosing, toggleIsClosing] = useToggle(false);

  const { id: currentUserId, isBillingContact } = useCurrentUser();
  const { organization } = useOrganization();
  const { intercomContactSalesEventName } = useApp();
  const { isPaidPlan } = useOrganizationSubscription();
  const { tr } = useTranslation();

  const handleDismissClick = () => {
    toggleIsClosing();
    onDismiss();
  };

  useEffect(() => {
    trackEvent('Viewed Charma legacy sunset modal', {
      user: currentUserId,
      isBillingContact,
      isPaidPlan,
      organization: organization.id,
    });
  }, []);

  const baseClassName = 'CharmaLegacySunsetModal';
  return (
    <WpModal isOpen={true} size="md" dismissOnBackdrop={false}>
      <WpModalBody padding={0}>
        <div className={`${baseClassName}__img-wrapper`}>
          <WpIllustration
            src={Illustrations.person_with_megaphone_with_purple_bg}
            className={`${baseClassName}__img-wrapper__img`}
          />
          <div className={`${baseClassName}__img-wrapper__close__wrapper__btn`}>
            <WpButton
              variant="tertiary"
              onClick={handleDismissClick}
              qaTest="dismiss-new-charma-sunset-modal"
              loading={isClosing}
            >
              <WpIcon name="times" />
            </WpButton>
          </div>
        </div>
      </WpModalBody>
      <WpModalBody>
        <WpText color="purple-800" bold>
          {tr('perform.assistants.sunset_modal.action_required')}
        </WpText>
        <WpText bold variant="bodyXxl">
          {tr('perform.assistants.sunset_modal.deactivation_title')}
        </WpText>
        <WpText>{tr('perform.assistants.sunset_modal.acquisition_message')}</WpText>
        <WpText>
          {tr('perform.assistants.sunset_modal.contract_message')}{' '}
          {isPaidPlan && tr('perform.assistants.sunset_modal.special_offer')}
        </WpText>

        {isBillingContact ? (
          <>
            <WpText>{tr('perform.assistants.sunset_modal.billing_contact_message')}</WpText>
            <WpText>
              {tr('perform.assistants.sunset_modal.contact_info')}{' '}
              <WpLink href="mailto:jklein@predictiveindex.com">jklein@predictiveindex.com</WpLink>.
            </WpText>
            <WpText>{tr('perform.assistants.sunset_modal.partnership_message')}</WpText>
          </>
        ) : (
          <WpContainer color="gray">
            <WpContainerBody>
              <WpText>{tr('perform.assistants.sunset_modal.non_billing_message')}</WpText>
              <WpText noMargin bold>
                {organization.billingContact.full_name}
              </WpText>

              <WpText noMargin>
                <WpLink href={`mailto:${organization.billingContact.email}`}>
                  {organization.billingContact.email}
                </WpLink>
              </WpText>
            </WpContainerBody>
          </WpContainer>
        )}
      </WpModalBody>
      {isBillingContact && (
        <WpModalFooter>
          <WpButton dataAttributes={{ 'data-intercom': intercomContactSalesEventName }}>
            {tr('perform.assistants.sunset_modal.connect_sales')}
          </WpButton>
        </WpModalFooter>
      )}
    </WpModal>
  );
};

export default CharmaLegacySunsetModal;
